import React, { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';

import { DonutChart } from '../../../../components/charts/DonutChart';
import { formatCurrency } from '../../../../components/charts/d3/format';
import { SpendCalendarCard } from '../../../../components/spend/SpendCalendarCard';
import { SpendCategoryAccordion } from '../../../../components/spend/SpendCategoryAccordion';
import { CreditCardDetailModal } from '../../../../components/spend/CreditCardDetailModal';
import { CreditCardsWidget } from '../../../../components/spend/CreditCardsWidget';
import { formatCardTitle } from '../../../../components/spend/creditCardFormat';
import { SpendCategoryTrends } from '../../../../components/spend/SpendCategoryTrends';
import { SpendCategoryTransactionsModal } from '../../../../components/spend/SpendCategoryTransactionsModal';
import type { SpendCategoryModalTarget } from '../../../../components/spend/spendCategoryModal';
import { SpendImportCta } from '../../../../components/spend/SpendImportCta';
import { SpendMonthNavigator } from '../../../../components/spend/SpendMonthNavigator';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { WebScreenBody } from '../../../../components/layout/WebScreenBody';
import { WebWidgetCell, WebWidgetGrid } from '../../../../components/layout/WebWidgetGrid';
import { ErrorRetry } from '../../../../components/ui/ErrorRetry';
import { GlassButton } from '../../../../components/ui/GlassButton';
import { GlassCard } from '../../../../components/ui/GlassCard';
import { RefreshButton } from '../../../../components/ui/RefreshButton';
import { WidgetRefreshScope } from '../../../../contexts/WidgetRefreshContext';
import { SampleDataBanner } from '../../../../components/ui/SampleDataBanner';
import { useHousehold } from '../../../../contexts/HouseholdContext';
import { useImportOnboarding } from '../../../../contexts/ImportOnboardingContext';
import { useSampleData } from '../../../../contexts/SampleDataContext';
import type { SpendStackParamList } from '../../../../core/navigation/types';
import { colors, spacing, typography } from '../../../../core/theme/tokens';
import { useAppRefresh } from '../../../../hooks/useAppRefresh';
import {
  formatMonthKeyLabel,
  useSpendBreakdown,
} from '../../../../hooks/useSpendBreakdown';
import type { HouseholdPeriodKind, SpendCategoryNode } from '../../../../types/financial';
import type { CardSpendRow } from '../../../../types/analytics';

type Props = NativeStackScreenProps<SpendStackParamList, 'SpendHome'>;

const PERIOD_OPTIONS: { id: HouseholdPeriodKind; label: string }[] = [
  { id: 'month', label: 'Month' },
  { id: 'year', label: 'Year' },
  { id: 'ytd', label: 'YTD' },
];

function currentMonthKey(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

function periodSubtitle(period: HouseholdPeriodKind, periodKey: string, totalSpend: number): string {
  const amount = formatCurrency(totalSpend);
  if (period === 'year') return `${amount} in ${periodKey}`;
  if (period === 'ytd') return `${amount} YTD ${periodKey}`;
  return `${amount} · ${formatMonthKeyLabel(periodKey)}`;
}

function sampleTxsForCategory(category: SpendCategoryNode) {
  return category.merchants.flatMap((merchant) =>
    merchant.transactions.map((tx) => ({
      id: tx.id,
      date: tx.date,
      merchant: tx.merchant || merchant.name,
      amount: tx.amount,
      note: tx.note,
    })),
  );
}

export function SpendHomeScreen({ navigation, route }: Props) {
  const { isSampleMode } = useSampleData();
  const { core } = useHousehold();
  const { openImportMore } = useImportOnboarding();
  const [period, setPeriod] = useState<HouseholdPeriodKind>('month');
  const [selectedMonth, setSelectedMonth] = useState(() =>
    isSampleMode && /^\d{4}-\d{2}$/.test(core.profile.asOfMonth)
      ? core.profile.asOfMonth
      : currentMonthKey(),
  );
  const [categoryTarget, setCategoryTarget] = useState<SpendCategoryModalTarget | null>(null);
  const [cardTarget, setCardTarget] = useState<CardSpendRow | null>(null);

  // Deep-link from Home allocation (“Last month” / period chip).
  useEffect(() => {
    const monthKey = route.params?.monthKey;
    if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) return;
    setPeriod('month');
    // Sample: don't adopt an empty calendar "now" deep-link over fixture asOfMonth.
    if (
      isSampleMode &&
      monthKey === currentMonthKey() &&
      /^\d{4}-\d{2}$/.test(core.profile.asOfMonth) &&
      core.profile.asOfMonth < monthKey
    ) {
      setSelectedMonth(core.profile.asOfMonth);
      return;
    }
    setSelectedMonth(monthKey);
  }, [route.params?.monthKey, isSampleMode, core.profile.asOfMonth]);

  const {
    categories,
    totalSpend,
    periodKey,
    isLive,
    isEmpty,
    loading,
    error,
    usedPeriodFallback,
    analyticsPeriod,
    refresh: refreshSpend,
  } = useSpendBreakdown({
    period,
    periodKey: period === 'month' ? selectedMonth : undefined,
  });
  const { refreshAll, refreshing } = useAppRefresh();
  const pageRefresh = () => {
    refreshSpend();
    void refreshAll('spend_page');
  };
  const viewingPastMonth =
    period === 'month' && selectedMonth < currentMonthKey() && !usedPeriodFallback;

  const openCategoryModal = (categoryId: string, fallbackName?: string, fallbackAmount?: number) => {
    const category = categories.find((c) => c.id === categoryId);
    if (!category && !fallbackName) return;
    setCategoryTarget({
      categorySlug: categoryId,
      categoryName: category?.name ?? fallbackName ?? categoryId,
      amount: category?.amount ?? fallbackAmount ?? 0,
      period: analyticsPeriod,
      periodKey,
      sampleTransactions:
        !isLive && category ? sampleTxsForCategory(category) : undefined,
    });
  };

  // Keep the month navigator aligned with the period actually shown (fixture remap / live fallback).
  useEffect(() => {
    if (period !== 'month' || !/^\d{4}-\d{2}$/.test(periodKey)) return;
    if (periodKey === selectedMonth) return;
    if (isLive && !usedPeriodFallback) return;
    setSelectedMonth(periodKey);
  }, [period, periodKey, selectedMonth, isLive, usedPeriodFallback]);

  return (
    <WebScreenBody>
      {isSampleMode && <SampleDataBanner />}

      <PageHeader
        title="Spend"
        subtitle={
          isLive
            ? periodSubtitle(period, periodKey, totalSpend)
            : `${periodSubtitle(period, periodKey, totalSpend)} · tap a category for transactions`
        }
        onRefresh={pageRefresh}
        refreshing={refreshing || loading}
      />
      <WidgetRefreshScope refresh={pageRefresh} refreshing={refreshing || loading}>
      <View style={styles.pageHeader}>
        {isLive && usedPeriodFallback ? (
          <Text style={styles.fallbackHint}>
            No spend in the current month — showing {formatMonthKeyLabel(periodKey)} from your
            import.
          </Text>
        ) : null}

        {isLive && (isEmpty ? !loading : true) ? (
          <SpendImportCta
            onImport={openImportMore}
            onShowAll={
              isEmpty
                ? undefined
                : () =>
                    navigation.navigate('TransactionList', {
                      mode: 'all',
                      period,
                      analyticsPeriod,
                      periodKey,
                    })
            }
          />
        ) : null}

        <View style={styles.periodRow}>
          {PERIOD_OPTIONS.map((option) => (
            <GlassButton
              key={option.id}
              variant="secondary"
              size="sm"
              selected={period === option.id}
              onPress={() => setPeriod(option.id)}
            >
              {option.label}
            </GlassButton>
          ))}
        </View>
        {period === 'month' ? (
          <SpendMonthNavigator monthKey={selectedMonth} onChange={setSelectedMonth} />
        ) : null}
      </View>

      {error ? (
        <ErrorRetry message={error} onRetry={pageRefresh} refreshing={refreshing || loading} />
      ) : null}

      {isLive && isEmpty && !loading && (
        <GlassCard style={styles.emptyCard}>
          <Text style={styles.emptyTitle}>
            {viewingPastMonth
              ? `No spending in ${formatMonthKeyLabel(selectedMonth)}`
              : 'No spending yet'}
          </Text>
          <Text style={styles.emptyBody}>
            {viewingPastMonth
              ? 'Try another month, or import statements that cover this period.'
              : 'Import screenshots or PDF statements to see categories and totals here.'}
          </Text>
        </GlassCard>
      )}

      {!isLive && categories.length > 0 && (
        <View style={styles.importRow}>
          <GlassButton
            variant="secondary"
            size="sm"
            onPress={() =>
              navigation.navigate('TransactionList', {
                mode: 'all',
                period,
              })
            }
          >
            Show all transactions
          </GlassButton>
        </View>
      )}

      {loading && categories.length === 0 && (
        <ActivityIndicator color={colors.accent} style={styles.loader} />
      )}

      <WebWidgetGrid>
        {totalSpend > 0 && (
          <WebWidgetCell span="full">
            <GlassCard>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>
                  {period === 'month' ? 'This month' : period === 'ytd' ? 'Year to date' : 'This year'}
                </Text>
                <RefreshButton compact />
              </View>
              <DonutChart
                segments={categories.map((c) => ({
                  id: c.id,
                  label: c.name,
                  value: c.amount,
                  color: c.color,
                }))}
                centerLabel="Spent"
                centerValue={formatCurrency(totalSpend)}
                onSegmentPress={(segment) => {
                  if (!segment.id) return;
                  openCategoryModal(segment.id, segment.label, segment.value);
                }}
              />
            </GlassCard>
          </WebWidgetCell>
        )}

        {!isLive || !isEmpty || loading ? (
          <WebWidgetCell span="full">
            <CreditCardsWidget
              period={analyticsPeriod}
              periodKey={periodKey}
              sampleCategories={categories}
              hasHouseholdSpend={!isEmpty}
              onOpenCard={setCardTarget}
            />
          </WebWidgetCell>
        ) : null}

        {isLive ? (
          <WebWidgetCell span="full">
            <SpendCalendarCard
              periodKey={periodKey}
              categories={categories.map((c) => ({
                id: c.id,
                name: c.name,
                color: c.color,
              }))}
              highlightMonthKey={period === 'month' ? periodKey : undefined}
              onDayPress={(date) => {
                if (period === 'month' && /^\d{4}-\d{2}/.test(date)) {
                  setSelectedMonth(date.slice(0, 7));
                }
              }}
            />
          </WebWidgetCell>
        ) : null}

        {isLive && period === 'month' ? (
          <WebWidgetCell span="full">
            <SpendCategoryTrends
              categories={categories.map((c) => ({
                id: c.id,
                name: c.name,
                color: c.color,
              }))}
              onFocusMonth={setSelectedMonth}
              onOpenCategoryMonth={(target) => {
                setSelectedMonth(target.periodKey);
                setCategoryTarget(target);
              }}
              onOpenAllMonth={(monthKey) => {
                setSelectedMonth(monthKey);
                navigation.navigate('TransactionList', {
                  mode: 'all',
                  period: 'month',
                  periodKey: monthKey,
                  analyticsPeriod: 'month',
                });
              }}
              onOpenMerchant={(categorySlug, merchantNormalized, monthKey) => {
                setSelectedMonth(monthKey);
                navigation.navigate('TransactionList', {
                  categoryId: categorySlug,
                  merchantId: merchantNormalized,
                  period: 'month',
                  periodKey: monthKey,
                  analyticsPeriod: 'month',
                });
              }}
            />
          </WebWidgetCell>
        ) : null}

        {categories.length > 0 && (
          <WebWidgetCell span="full">
            <SpendCategoryAccordion
              categories={categories}
              onMerchantPress={(categoryId, merchantId) =>
                navigation.navigate('TransactionList', { categoryId, merchantId, period })
              }
              onCategoryPress={openCategoryModal}
            />
          </WebWidgetCell>
        )}
      </WebWidgetGrid>
      </WidgetRefreshScope>

      <CreditCardDetailModal
        card={cardTarget}
        period={analyticsPeriod}
        periodKey={periodKey}
        isLive={isLive}
        sampleCategories={categories}
        onClose={() => setCardTarget(null)}
        onOpenCategory={(slug, name, amount) => {
          const category = categories.find((c) => c.id === slug);
          setCategoryTarget({
            categorySlug: slug,
            categoryName: name,
            amount,
            period: analyticsPeriod,
            periodKey,
            spendAccountId: cardTarget?.spendAccountId,
            sampleTransactions:
              !isLive && category ? sampleTxsForCategory(category) : undefined,
          });
        }}
        onViewAll={(card) => {
          setCardTarget(null);
          navigation.navigate('TransactionList', {
            mode: 'all',
            period,
            analyticsPeriod,
            periodKey,
            spendAccountId: card.spendAccountId,
            spendAccountLabel: formatCardTitle(card),
          });
        }}
      />
      <SpendCategoryTransactionsModal
        target={categoryTarget}
        onClose={() => setCategoryTarget(null)}
      />
    </WebScreenBody>
  );
}

const styles = StyleSheet.create({
  pageHeader: {
    marginBottom: spacing.sm,
    gap: spacing.sm,
  },
  pageTitle: {
    ...typography.title,
    color: colors.text,
    fontSize: 26,
  },
  pageSub: {
    ...typography.body,
    color: colors.textMuted,
  },
  fallbackHint: {
    ...typography.caption,
    color: colors.accent,
  },
  periodRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  importRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  loader: {
    marginVertical: spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  sectionTitle: {
    ...typography.headline,
    color: colors.text,
  },
  emptyCard: {
    marginBottom: spacing.md,
    gap: spacing.sm,
    borderColor: 'rgba(61, 214, 164, 0.25)',
  },
  emptyTitle: {
    ...typography.headline,
    color: colors.text,
  },
  emptyBody: {
    ...typography.body,
    color: colors.textMuted,
  },
  errorCard: {
    marginBottom: spacing.md,
    borderColor: colors.warning,
  },
  errorText: {
    ...typography.body,
    color: colors.warning,
  },
  retryHint: {
    ...typography.caption,
    color: colors.accent,
    fontWeight: '700',
    marginTop: 6,
  },
});
